// ✅ Trekkin.app — useGPSHybrid.js v3.20.0
// 🧭 iOS Perfeito (inalterado) + ANDROID BG REAL (ForegroundService) CLEAN + LiveTrack 5s + BlackBox Firebase
// ------------------------------------------------------------------
// 🔹 Base = v3.18.0 (mantém comportamento) + BG REAL Android (sem lixo)
// 🔹 ANDROID BG REAL:
//      • startTracking() → inicia ForegroundService (se plugin existir)
//      • stopTracking()  → pára ForegroundService
//      • Recebe fixes via notifyListeners("gpsFix")
// 🔹 iOS/Web:
//      • Mantém bridge "native-gps-update" + "native:location"
//      • Mantém fallback por visibilitychange (APENAS web/iOS)
// ------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Geolocation } from "@capacitor/geolocation";

// 🔹 Firebase + Auth (só para BlackBox)
import { db } from "../firebaseConfig";
import { ref, set, onValue, off } from "firebase/database";
import { useAuthStore } from "../stores/useAuthStore.jsx";
import { auth } from "../firebaseConfig";
import { usePanelsStore } from "../stores/usePanelsStore.jsx";
import { useShareStore } from "../stores/useShareStore.jsx";
import { useGroupStore } from "../stores/useGroupStore.jsx";
import { useEventsStore } from "../stores/useEventsStore.jsx";
import { logGPSFixRemote } from "../functions/gpsLogger";
import { logEvent } from "../stores/useLogsStore.jsx";

import { startNativeCompass, stopNativeCompass } from "../nativeCompassBridge";

// ===================================================
// 🔕 LOGS CONTROLADOS (FG ONLY) — sem spam
// ===================================================
const fgLog = (...args) => {
  try {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      console.log(...args);
    }
  } catch {}
};

const fgWarn = (...args) => {
  try {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      console.warn(...args);
    }
  } catch {}
};

// ---------------------------------------------------
// Configuração
// ---------------------------------------------------
// iOS overrides:
//  - velocidade mínima para "moving"
//  - maximumAge para getCurrentPosition/watchPosition
//  - intervalo de emissão de pontos
const CFG = {
  // Qualidade / accuracy
  resumeRequiredAccuracy: 50,
  hardAccuracyReject: 200,

  // Movimento / jitter
  minStepDistance: 2,
  jitterIgnoreDistance: 3,
  speedMovingThreshold: 0.25,
  iosSpeedMovingThreshold: 0.2,
  androidIosSpeedMovingThreshold: 0.22,
  speedStopGraceSeconds: 10,

  // 🔥 GAP REDUZIDO
  longPauseGapSeconds: 25,
  longPauseGapDistance: 200,

  // Fallback PWA
  fallbackIntervalSeconds: 8,
  gpsTimeoutMs: 12000,
  maximumAgeMs: 1000,
  iosMaximumAgeMs: 1500,

  // Altimetria
  elevationSmoothingWindow: 5,
  elevationMinDelta: 1,

  // Anti-zig-zag geral
  smallStepDistance: 3,
  lowSpeedThreshold: 1,
  badAccuracy: 80,

  // 🌟 Android filtros (tuning 2025-12-30: menos zigzag)
  androidRelaxedFilters: true,
  androidMaxAcceptableAcc: 76,
  androidWarmupRequiredAcc: 86,
  androidWarmupFixes: 2,
  androidVerySmallStep: 3,
  androidStationarySpeedThreshold: 0.4,
  androidStationaryMaxAcc: 57,
  androidStationaryMaxStep: 10,
  androidSlowSpeedThreshold: 1.5,
  androidBigJumpWhenSlow: 40,
  androidSmoothWindow: 7,
  androidMaxSpeedMps: 55.6, // 200 km/h
  androidMaxAccelMps2: 8,   // accel cap to avoid sudden jumps

  // LiveTrack
  emitIntervalSeconds: 1.5,
  iosEmitIntervalSeconds: 1.2,

  // iOS smoothing (mais suave sem mexer no Android)
  iosJitterIgnoreDistance: 1.6,
  iosMicroStepDistance: 1.6,
  androidIosJitterIgnoreDistance: 2.0,
  androidIosMicroStepDistance: 1.8,

  // ✅ Android BG service params (relógio suíço)
  androidService: {
    intervalMs: 2000,
    fastestMs: 1000,
    minDistanceM: 3,
    highAccuracy: true,
  },
};

const BG_BUFFER_FLUSH_INTERVAL_MS = 5000;

// ===============================
// ANDROID SEQUENCE CONTROL
// ===============================
const LAST_SEQ_KEY = "__gps_last_seq";
const LAST_ANDROID_TS_KEY = "__gps_last_android_ts";
const LAST_ANDROID_SEQ_KEY = "__gps_last_android_seq";

function getLastAndroidTs() {
  if (typeof localStorage === "undefined") return 0;
  const raw = Number(localStorage.getItem(LAST_ANDROID_TS_KEY) || 0);
  return Number.isFinite(raw) ? raw : 0;
}

function setLastAndroidTs(ts) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(LAST_ANDROID_TS_KEY, String(ts || 0));
  } catch {}
}

function getLastAndroidSeq() {
  if (typeof localStorage === "undefined") return 0;
  const raw = Number(localStorage.getItem(LAST_ANDROID_SEQ_KEY) || 0);
  return Number.isFinite(raw) ? raw : 0;
}

function setLastAndroidSeq(seq) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(LAST_ANDROID_SEQ_KEY, String(seq || 0));
  } catch {}
}

// ---------------------------------------------------
// Utils
// ---------------------------------------------------
function haversine(a, b) {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const nowTs = () => Date.now();
const normalizeTs = (ts) => {
  const n = Number(ts) || 0;
  if (!n) return nowTs();
  return n < 1e12 ? n * 1000 : n;
};
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
const clamp = (arr, max) => {
  while (arr.length > max) arr.shift();
  return arr;
};

function getLastSeq() {
  try {
    return Number(localStorage.getItem(LAST_SEQ_KEY) || 0);
  } catch {
    return 0;
  }
}

function setLastSeq(v) {
  try {
    localStorage.setItem(LAST_SEQ_KEY, String(v));
  } catch {}
}

// ---------------------------------------------------
// 🧭 Compass smoothing (EMA)
// ---------------------------------------------------
function smoothHeading(prev, next, alpha = 0.15) {
  if (prev == null) return next;

  let delta = next - prev;

  // normalizar ângulo (-180 → 180)
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;

  return (prev + alpha * delta + 360) % 360;
}

const pageHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";
const liveLog = (...args) => {
  try {
    if (localStorage.getItem("gpsLiveDebug") === "1") {
      console.log("[GPS-LIVE]", ...args);
    }
  } catch {}
};

// Detecta se estamos numa app nativa Capacitor (Android/iOS)
const isNativeCapacitor =
  typeof window !== "undefined" && Capacitor?.isNativePlatform?.() === true;

const platform = Capacitor?.getPlatform?.() || "";
let isAndroidPlatform = platform === "android";

// Fallback extra: Android Chrome / WebView (UA)
if (!isAndroidPlatform && typeof navigator !== "undefined") {
  if (/Android/i.test(navigator.userAgent || "")) isAndroidPlatform = true;
}

const useAndroidIosFilters = () => {
  if (!isAndroidPlatform) return false;
  try {
    const val = localStorage.getItem("gpsAndroidIosFilters");
    if (val == null) return true;
    return val === "1";
  } catch {
    return true;
  }
};

const getSpeedMovingThreshold = () =>
  isAndroidPlatform
    ? useAndroidIosFilters()
      ? CFG.androidIosSpeedMovingThreshold
      : CFG.speedMovingThreshold
    : CFG.iosSpeedMovingThreshold;
const getMaximumAgeMs = () =>
  isAndroidPlatform ? CFG.maximumAgeMs : CFG.iosMaximumAgeMs;
const getEmitIntervalMs = () =>
  (isAndroidPlatform ? CFG.emitIntervalSeconds : CFG.iosEmitIntervalSeconds) * 1000;
const getJitterIgnoreDistance = () =>
  isAndroidPlatform
    ? useAndroidIosFilters()
      ? CFG.androidIosJitterIgnoreDistance
      : CFG.jitterIgnoreDistance
    : CFG.iosJitterIgnoreDistance;

const getMicroStepDistance = () =>
  isAndroidPlatform && useAndroidIosFilters()
    ? CFG.androidIosMicroStepDistance
    : CFG.iosMicroStepDistance;

// Resolver plugin Android (GPSNativePlugin/GPSNative)
function getAndroidGPSPlugin() {
  try {
    return (
      window?.Capacitor?.Plugins?.GPSNativePlugin ||
      window?.Capacitor?.Plugins?.GPSNative ||
      Capacitor?.Plugins?.GPSNativePlugin ||
      Capacitor?.Plugins?.GPSNative ||
      null
    );
  } catch {
    return null;
  }
}

function getActiveEventForGroupSafe(groups, gid, events) {
  if (!gid) return { activeEvent: null, isLive: false };

  const groupsArr = Array.isArray(groups)
    ? groups
    : groups && typeof groups === "object"
    ? Object.values(groups)
    : [];

  const group =
    groupsArr.find(
      (g) =>
        g.id === gid || g.groupId === gid || g.firebaseId === gid || g.key === gid
    ) || null;

  if (!group) return { activeEvent: null, isLive: false };

  const activeEventId =
    group.activeEventId || group.activeEvent || group.eventId || null;

  const eventsArr = Array.isArray(events)
    ? events
    : events && typeof events === "object"
    ? Object.values(events)
    : [];

  const activeEvent =
    activeEventId && eventsArr.length
      ? eventsArr.find((ev) => ev.id === activeEventId) || null
      : null;

  if (!activeEvent && activeEventId) {
    return {
      activeEvent: { id: activeEventId, isLive: true, __fallback: true },
      isLive: true,
    };
  }

  const isLive = !!activeEvent?.isLive;
  return { activeEvent, isLive };
}

// ====================================================================
// 🧠 Hook principal
// ====================================================================
export default function useGPSHybrid({ onUpdate } = {}) {
  const watchId = useRef(null); // navigator.geolocation ID (web)
  const capWatchId = useRef(null); // Capacitor Geolocation ID
  const fbTimer = useRef(null);
  const lastMotionTs = useRef(0);
  const headingRef = useRef(null);
  // ---------------------------------------------------
  // 🧭 Compass (iOS + Android) — estado interno
  // ---------------------------------------------------
  const rawHeadingRef = useRef(null);
  const smoothHeadingRef = useRef(null);

  const altWin = useRef([]);
  const lastBelow = useRef(null);
  const waitingAccurate = useRef(false);
  const prevSmoothAlt = useRef(null);

  const fixCountRef = useRef(0);
  const isNativeRef = useRef(false); // a receber posições de fonte nativa (bridge/plugin)
  const lastSeqRef = useRef(getLastSeq());
  const lastAndroidTsRef = useRef(getLastAndroidTs());
  const lastAndroidSeqRef = useRef(getLastAndroidSeq());

  const androidSmoothWindowRef = useRef([]);
  const lastEmitTs = useRef(0);
  const androidBufferQueueRef = useRef([]);
  const androidBufferDrainTimerRef = useRef(null);
  const androidBufferDrainEmitRef = useRef(false);
  const lastAndroidBufferLogTsRef = useRef(0);
  const lastAndroidBufferInfoLogTsRef = useRef(0);
  const lastAndroidSeqDropLogTsRef = useRef(0);
  const androidSeqResetLogTsRef = useRef(0);
  

  // ✅ Android BG service state (sem “visibilidade”)
  const bgServiceActiveRef = useRef(false);
  const appIsActiveRef = useRef(true); // App em foreground/background (App.addListener)
  const bgListenerHandleRef = useRef(null);
  const lastAndroidFixTsRef = useRef(0);
  const androidWarmupStartedRef = useRef(false);
  const androidWarmupRetryTimerRef = useRef(null);
  const androidStartupRetryTimerRef = useRef(null);
  const androidStartupRetryCountRef = useRef(0);
  const androidLastNativeBufferTsRef = useRef(0);
  const backgroundFlushIntervalRef = useRef(null);
  const androidLiveDispatchRef = useRef(false);
  const liveTokenRef = useRef(null);
  const liveTargetsTimerRef = useRef(null);
  const androidBatteryOptPromptedRef = useRef(false);

  const [isTracking, setIsTracking] = useState(false);
  // O estado React pode ficar um render atrás durante a troca entre gravação
  // e localização normal. A ref é a fonte síncrona para iniciar/parar watchers.
  const isTrackingRef = useRef(false);
  const [lastFix, setLastFix] = useState(null);
  const [lastLiveFix, setLastLiveFix] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const lastLiveFixTsRef = useRef(0);
  const isRecording = usePanelsStore((s) => s.isRecording);
  const isRecordingPaused = usePanelsStore((s) => s.isRecordingPaused);
  const pausedByRecordingRef = useRef(false);

  useEffect(() => {
    return () => {
      if (androidBufferDrainTimerRef.current) {
        clearTimeout(androidBufferDrainTimerRef.current);
        androidBufferDrainTimerRef.current = null;
      }
    };
  }, []);

  // 🧭 ANDROID — obter posição inicial e aquecer GPS ao abrir a app
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== "android") return;

    const plugin =
      window?.Capacitor?.Plugins?.GPSNativePlugin ||
      window?.Capacitor?.Plugins?.GPSNative;

    if (plugin?.getCurrentPositionOnce) {
      plugin.getCurrentPositionOnce();
      logEvent("system", "Android warmup: getCurrentPositionOnce");
    }

    const kickAndroidGPS = (label) => {
      if (lastAndroidFixTsRef.current) return;
      startAndroidBGServiceIfPossible();
      const retryPlugin = getAndroidGPSPlugin();
      retryPlugin?.getCurrentPositionOnce?.();
      retryPlugin?.flushBufferedLocations?.();
      logEvent("system", `Android warmup: ${label}`);
    };

    const scheduleStartupRetry = () => {
      if (lastAndroidFixTsRef.current) return;
      if (androidStartupRetryCountRef.current >= 6) return;
      if (androidStartupRetryTimerRef.current) return;

      androidStartupRetryTimerRef.current = setTimeout(() => {
        androidStartupRetryTimerRef.current = null;
        androidStartupRetryCountRef.current += 1;

        startAndroidBGServiceIfPossible();

        const retryPlugin = getAndroidGPSPlugin();
        retryPlugin?.getCurrentPositionOnce?.();
        logEvent("system", "Android warmup: startup retry", {
          attempt: androidStartupRetryCountRef.current,
        });

        scheduleStartupRetry();
      }, 2000);
    };

    if (androidWarmupRetryTimerRef.current) {
      clearTimeout(androidWarmupRetryTimerRef.current);
    }
    androidWarmupRetryTimerRef.current = setTimeout(() => {
      try {
        plugin?.getCurrentPositionOnce?.();
        logEvent("system", "Android warmup: retry getCurrentPositionOnce");
      } catch {}
    }, 6000);

    const warmupKickTimer = setTimeout(() => {
      kickAndroidGPS("delayed kick");
    }, 1500);

    const warmupKickTimer2 = setTimeout(() => {
      kickAndroidGPS("delayed kick 2");
    }, 3500);

    if (!androidWarmupStartedRef.current) {
      androidWarmupStartedRef.current = true;
      startAndroidBGServiceIfPossible();
      androidStartupRetryCountRef.current = 0;
      scheduleStartupRetry();
    }

    return () => {
      clearTimeout(warmupKickTimer);
      clearTimeout(warmupKickTimer2);
    };
  }, []);

  const pathRef = useRef([]);
  const [pathVersion, setPathVersion] = useState(0);
  const lastPathUiEmitRef = useRef(0);

  const distRef = useRef(0);
  const movRef = useRef(0);
  const stopRef = useRef(0);
  const gainRef = useRef(0);
  const lossRef = useRef(0);

  const tickTs = useRef(null);

  const path = useMemo(() => [...pathRef.current], [pathVersion]);

  const isBg = () =>
    typeof document !== "undefined" && document.visibilityState === "hidden";

  // ---------------------------------------------------
  // 🔎 BlackBox config por utilizador
  // ---------------------------------------------------
  const debugConfigRef = useRef({
    initialized: false,
    enabled: false,
    uid: null,
    session: null,
  });

  if (!debugConfigRef.current.initialized) {
    let enabled = false;
    let uid = null;

    try {
      const auth = useAuthStore.getState();
      const user = auth?.user || auth;

      uid = user?.uid || user?.id || auth?.uid || auth?.userId || null;

      const flag =
        user?.gpsDebug ?? user?.debugGPS ?? auth?.gpsDebug ?? auth?.debugGPS ?? null;

      let lsFlag = null;
      try {
        if (typeof window !== "undefined") lsFlag = window.localStorage.getItem("gpsDebug");
      } catch {}

      enabled =
        flag === true ||
        flag === "1" ||
        flag === "true" ||
        lsFlag === "1" ||
        lsFlag === "true";
    } catch {
      enabled = false;
      uid = null;
    }

    debugConfigRef.current = {
      initialized: true,
      enabled,
      uid,
      session: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };

    if (enabled && uid) {
      fgLog(`🧪 [GPS DEBUG] BlackBox ON uid=${uid} (sessão=${debugConfigRef.current.session})`);
    }
  }

  // ---------------------------------------------------
  // 📝 BlackBox Firebase (nunca pode partir o GPS)
  // ---------------------------------------------------
  function refreshDebugConfig() {
    try {
      const auth = useAuthStore.getState();
      const user = auth?.user || auth;

      const uid = user?.uid || user?.id || auth?.uid || auth?.userId || null;
      const flag =
        user?.gpsDebug ?? user?.debugGPS ?? auth?.gpsDebug ?? auth?.debugGPS ?? null;

      let lsFlag = null;
      try {
        if (typeof window !== "undefined") lsFlag = window.localStorage.getItem("gpsDebug");
      } catch {}

      const enabled =
        flag === true ||
        flag === "1" ||
        flag === "true" ||
        lsFlag === "1" ||
        lsFlag === "true";

      const cfg = debugConfigRef.current;
      const wasEnabled = cfg.enabled;

      cfg.uid = uid;
      cfg.enabled = enabled;

      if (enabled && !cfg.session) {
        cfg.session = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }

      if (!wasEnabled && enabled && cfg.uid) {
        fgLog(`🧪 [GPS DEBUG] BlackBox ON uid=${cfg.uid} (sessão=${cfg.session})`);
      }
    } catch {}
  }

  function logGPS(fix, source, meta = {}) {
    try {
      if (usePanelsStore.getState().recordingMode === "expedition") return;
      refreshDebugConfig();
      const cfg = debugConfigRef.current;
      if (!cfg?.enabled || !cfg.uid || !db) return;

      const ts = fix?.ts || nowTs();
      const d = new Date(ts);

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const dayKey = `${yyyy}${mm}${dd}`;

      const hh = String(d.getHours()).padStart(2, "0");
      const nn = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      const ms = String(d.getMilliseconds()).padStart(3, "0");
      const timeKey = `${hh}-${nn}-${ss}-${ms}`;

      const basePath = `/logs/gps/${cfg.uid}/${dayKey}/${timeKey}`;

      const payload = {
        src: source,
        lat: fix?.lat ?? null,
        lon: fix?.lon ?? null,
        alt: fix?.alt ?? null,
        acc: fix?.acc ?? null,
        spd: fix?.speed ?? null,
        ts,
        bg: pageHidden(),
        session: cfg.session,
        platform: {
          android: isAndroidPlatform,
          nativeCapacitor: isNativeCapacitor,
        },
        totals: {
          dist: distRef.current,
          moving: movRef.current,
          stopped: stopRef.current,
          gain: gainRef.current,
          loss: lossRef.current,
        },
        cfg: {
          hardAccuracyReject: CFG.hardAccuracyReject,
          androidMaxAcceptableAcc: CFG.androidMaxAcceptableAcc,
          badAccuracy: CFG.badAccuracy,
          jitterIgnoreDistance: CFG.jitterIgnoreDistance,
        },
        meta,
      };

      set(ref(db, basePath), payload).catch(() => {});
    } catch {}
  }

  // ---------------------------------------------------
  // Emit (throttle 5s)
  // ---------------------------------------------------
  function emit(forceFix, options = {}) {
    const { force = false } = options;
    const f = forceFix || lastFix;

    if (!f && !force) return;

    const now = nowTs();
    if (!force) {
      const last = lastEmitTs.current;
      const minDelta = getEmitIntervalMs();
      if (last && now - last < minDelta) return;
    }

    lastEmitTs.current = now;

    if (f) {
      try {
        onUpdate?.({
          lat: f.lat,
          lon: f.lon,
          ele: f.alt ?? 0,
          acc: f.acc ?? null,
          spd: f.speed ?? 0,
          ts: f.ts,
          heading: headingRef.current,
        });
      } catch {}
    }

    try {
      const detail = {
        isTracking,
        lastFix: f || lastFix,
        path: pathRef.current,
        totalDistance: distRef.current,
        movingTime: movRef.current,
        stoppedTime: stopRef.current,
        elevationGain: gainRef.current,
        elevationLoss: lossRef.current,
        accuracyNow: accuracy,
        heading: headingRef.current,
      };

      window.dispatchEvent(new CustomEvent("gps:update", { detail }));
    } catch {}
  }

  // ---------------------------------------------------
  // Fallback (browser/PWA) — igual ao estável
  // ---------------------------------------------------
  function startFallback() {
    if (fbTimer.current) return;
    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      isNativeRef.current ||
      isNativeCapacitor
    ) {
      return;
    }

    fbTimer.current = setInterval(() => {
      if (!isTracking) return;

      navigator.geolocation.getCurrentPosition(onSuccess, () => {}, {
        enableHighAccuracy: true,
        timeout: CFG.gpsTimeoutMs,
        maximumAge: getMaximumAgeMs(),
      });
    }, CFG.fallbackIntervalSeconds * 1000);
  }

  function stopFallback() {
    if (fbTimer.current) clearInterval(fbTimer.current);
    fbTimer.current = null;
  }

  // ---------------------------------------------------
  // Motion EXACT — acorda tracking (mantido)
  // ---------------------------------------------------
  function enableMotionAwake() {
    try {
      window.addEventListener("devicemotion", (ev) => {
        const a = ev.accelerationIncludingGravity;
        if (!a) return;

        const total = Math.abs(a.x) + Math.abs(a.y) + Math.abs(a.z);
        if (total > 1.2) {
          const now = nowTs();
          if (now - lastMotionTs.current > 1500) {
            lastMotionTs.current = now;
            waitingAccurate.current = true;
          }
        }
      });
    } catch {}
  }

  // ---------------------------------------------------
  // Capacitor Geolocation (native) — fallback se não houver plugin Android
  // ---------------------------------------------------
  async function startCapacitorWatch() {
    if (!isNativeCapacitor) return;

    try {
      const perm = await Geolocation.checkPermissions();
      if (perm?.location !== "granted") {
        const req = await Geolocation.requestPermissions();
        if (req?.location !== "granted") {
          fgWarn("⚠️ [GPS] Permissão de localização NÃO concedida (Capacitor).");
          return;
        }
      }
    } catch (err) {
      fgWarn("⚠️ [GPS] Erro permissões (Capacitor):", err);
      return;
    }

    try {
      const id = await Geolocation.watchPosition(
        {
          enableHighAccuracy: true,
          timeout: CFG.gpsTimeoutMs,
          maximumAge: getMaximumAgeMs(),
        },
        (position, err) => {
          if (err) {
            tickTime(null);
            emit();
            return;
          }
          if (!position || !position.coords) return;

          const c = position.coords;
          const fix = {
            lat: c.latitude,
            lon: c.longitude,
            alt: Number.isFinite(c.altitude) ? c.altitude : null,
            acc: Number.isFinite(c.accuracy) ? c.accuracy : null,
            speed: Number.isFinite(c.speed) ? c.speed : null,
            ts: position.timestamp || nowTs(),
          };

          handleFix(fix, "native-capacitor");
        }
      );

      capWatchId.current = id;
      logEvent("system", "Capacitor watchPosition start");
    } catch (err) {
      fgWarn("⚠️ [GPS] Erro ao iniciar watch nativo Capacitor:", err);
      logEvent("error", "Capacitor watchPosition erro", err?.message || err);
    }
  }

  function stopCapacitorWatch() {
    if (!capWatchId.current) return;
    try {
      Geolocation.clearWatch({ id: capWatchId.current });
    } catch {}
    capWatchId.current = null;
  }

  // ---------------------------------------------------
  // ✅ ANDROID BG REAL (ForegroundService) — start/stop só aqui
  // ---------------------------------------------------
  function startAndroidBGServiceIfPossible() {
    try {
      if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return false;

      const plugin = getAndroidGPSPlugin();
      if (!plugin?.startTracking) return false;

      if (bgServiceActiveRef.current) return true;

      bgServiceActiveRef.current = true;

      liveLog("Android BG start", { cfg: CFG.androidService });
      logGPS(null, "android-bg", { event: "startForegroundService", ...CFG.androidService });
      logEvent("system", "Android BG start", { ...CFG.androidService });
      fgLog("🔥 [ANDROID BG] ForegroundService ON");

      plugin.startTracking?.({
        intervalMs: CFG.androidService.intervalMs,
        fastestMs: CFG.androidService.fastestMs,
        minDistanceM: CFG.androidService.minDistanceM,
        highAccuracy: CFG.androidService.highAccuracy,
      });

      return true;
    } catch {
      bgServiceActiveRef.current = false;
      return false;
    }
  }

  function stopAndroidBGServiceIfPossible() {
    try {
      if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

      const plugin = getAndroidGPSPlugin();
      if (!plugin) return;

      liveLog("Android BG stop");
      logGPS(null, "android-bg", { event: "stopForegroundService" });
      logEvent("system", "Android BG stop");

      plugin?.stopTracking?.();
      plugin?.stopForeground?.(); // compat

      bgServiceActiveRef.current = false;
      fgLog("🔥 [ANDROID BG] ForegroundService OFF");
    } catch {
      bgServiceActiveRef.current = false;
    }
  }

  const requestAndroidForegroundPermission = useCallback(async () => {
    try {
      if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

      const plugin = getAndroidGPSPlugin();
      if (!plugin?.checkPermissions) return;

      let perm = await plugin.checkPermissions();
      if (perm?.location !== "granted") {
        await plugin.requestPermissions?.();
      }
    } catch (err) {
      fgWarn("⚠️ [GPS] Android foreground permission request failed:", err);
    }
  }, [isNativeCapacitor]);

  const requestAndroidAlwaysPermission = useCallback(async () => {
    try {
      if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

      const plugin = getAndroidGPSPlugin();
      if (!plugin?.checkPermissions) return;

      await requestAndroidForegroundPermission();
      const perm = await plugin.checkPermissions?.();

      if (perm?.background !== "granted") {
        await plugin.requestBackgroundLocation?.();
      }
    } catch (err) {
      fgWarn("⚠️ [GPS] Android background permission request failed:", err);
    }
  }, [isNativeCapacitor, requestAndroidForegroundPermission]);

  // ---------------------------------------------------
  // Start
  // ---------------------------------------------------
  function startTracking() {
    // 🔁 Sincronizar sempre o último seq persistido
    lastSeqRef.current = getLastSeq();
    if (isTrackingRef.current) return;

    isTrackingRef.current = true;
    setIsTracking(true);
    lastBelow.current = null;
    waitingAccurate.current = false;
    tickTs.current = nowTs();
    fixCountRef.current = 0;
    lastEmitTs.current = 0;

    // ⚠️ NÃO limpar path se vier de background
    if (!bgServiceActiveRef.current) {
      pathRef.current = [];
      distRef.current = movRef.current = stopRef.current = 0;
      gainRef.current = lossRef.current = 0;
      altWin.current = [];
      prevSmoothAlt.current = null;
      androidSmoothWindowRef.current = [];
      lastAndroidTsRef.current = 0;
      setLastAndroidTs(0);
    }

    // limpar watchers anteriores
    if (watchId.current != null) {
      try {
        navigator.geolocation.clearWatch(watchId.current);
      } catch {}
      watchId.current = null;
    }
    stopCapacitorWatch();
    stopFallback();

    const isAndroidNative = isNativeCapacitor && Capacitor.getPlatform() === "android";

    // ✅ ANDROID: se plugin existe, ele é o “clock” principal (FG+BG)
    let usingAndroidPlugin = false;
    if (isAndroidNative) {
      void requestAndroidForegroundPermission();
      usingAndroidPlugin = startAndroidBGServiceIfPossible();
      try {
        const plugin = getAndroidGPSPlugin();
        plugin?.getCurrentPositionOnce?.();
      } catch {}

      // 🧭 ANDROID — bússola nativa (mantido)
      try {
        startNativeCompass((heading) => {
          if (typeof heading !== "number") return;

          // ❌ Não rodar mapa se estiver parado
          if (
            lastFix &&
            (lastFix.speed ?? 0) < 0.6 && // ~2 km/h
            headingRef.current != null
          ) {
            return;
          }

          rawHeadingRef.current = heading;

          smoothHeadingRef.current = smoothHeading(
            smoothHeadingRef.current,
            heading
          );

          headingRef.current = smoothHeadingRef.current;
        });
      } catch {}
    }

    // Capacitor Geolocation só como fallback (iOS nativo, ou Android sem plugin)
    if (isNativeCapacitor && (!isAndroidNative || !usingAndroidPlugin)) {
      startCapacitorWatch();
    }

    // Web/PWA watchPosition
    const hasNavigator = typeof navigator !== "undefined" && !!navigator.geolocation;
    if (hasNavigator && !isNativeCapacitor) {
      try {
        watchId.current = navigator.geolocation.watchPosition(onSuccess, onFail, {
          enableHighAccuracy: true,
          timeout: CFG.gpsTimeoutMs,
          maximumAge: getMaximumAgeMs(),
        });
      } catch {
        navigator.geolocation.getCurrentPosition(onSuccess, onFail, {
          enableHighAccuracy: true,
          timeout: CFG.gpsTimeoutMs,
          maximumAge: getMaximumAgeMs(),
        });
      }

      if (pageHidden()) startFallback();
    }

    emit();
  }

  // ---------------------------------------------------
  // Stop
  // ---------------------------------------------------
  function stopTracking() {
    isTrackingRef.current = false;
    setIsTracking(false);

    stopAndroidBGServiceIfPossible();

    if (watchId.current != null) {
      try {
        navigator.geolocation.clearWatch(watchId.current);
      } catch {}
    }
    watchId.current = null;
    rawHeadingRef.current = null;
    smoothHeadingRef.current = null;
    headingRef.current = null;

    // 🧭 ANDROID — parar bússola nativa
    try {
      if (Capacitor.getPlatform() === "android") stopNativeCompass();
    } catch {}

    stopCapacitorWatch();
    stopFallback();

    emit(lastFix, { force: true });
  }

  useEffect(() => {
    if (isRecording && isRecordingPaused && isTracking) {
      pausedByRecordingRef.current = true;
      stopTracking();
      return;
    }
    if (isRecording && !isRecordingPaused && pausedByRecordingRef.current && !isTracking) {
      pausedByRecordingRef.current = false;
      startTracking();
      return;
    }
    if (!isRecording) {
      pausedByRecordingRef.current = false;
    }
  }, [isRecording, isRecordingPaused, isTracking]);

  // ---------------------------------------------------
  // Handlers (web)
  // ---------------------------------------------------
  function onFail() {
    tickTime(null);
    emit();
  }

  function onSuccess(pos) {
    if (isNativeRef.current) return;

    const c = pos.coords;
    const fix = {
      lat: c.latitude,
      lon: c.longitude,
      alt: Number.isFinite(c.altitude) ? c.altitude : null,
      acc: Number.isFinite(c.accuracy) ? c.accuracy : null,
      speed: Number.isFinite(c.speed) ? c.speed : null,
      ts: pos.timestamp || nowTs(),
    };

    handleFix(fix, "browser");
  }

  // ---------------------------------------------------
  // ✅ ANDROID BG listener (notifyListeners("gpsFix"))
  // ---------------------------------------------------
  useEffect(() => {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

    const plugin = getAndroidGPSPlugin();
    if (!plugin?.addListener) return;

    let alive = true;

    try {
      plugin
        .addListener("gpsFix", (payload) => {
          if (!alive) return;
          if (typeof payload?.lat !== "number" || typeof payload?.lon !== "number") return;

          liveLog("Android gpsFix", {
            lat: payload.lat,
            lon: payload.lon,
            acc: payload.acc,
            ts: payload.ts,
            seq: payload.seq ?? payload.sequence,
          });

          isNativeRef.current = true;

          bgServiceActiveRef.current = true;           // serviço está vivo
          lastAndroidFixTsRef.current = Date.now();    // heartbeat real
          if (androidStartupRetryTimerRef.current) {
            clearTimeout(androidStartupRetryTimerRef.current);
            androidStartupRetryTimerRef.current = null;
          }
          androidStartupRetryCountRef.current = 0;

          const seqRaw = payload?.seq ?? payload?.sequence;
          const seqNum = Number(seqRaw);
          const fix = {
            lat: payload.lat,
            lon: payload.lon,
            alt: payload.ele ?? payload.alt ?? null,
            acc: payload.acc ?? null,
            speed: payload.spd ?? payload.speed ?? null,
            ts: normalizeTs(payload.ts),
            seq: Number.isFinite(seqNum) ? seqNum : null,
          };

          handleFix(fix, "android-fg");
        })
        .then((h) => {
          bgListenerHandleRef.current = h;
        })
        .catch(() => {});
    } catch {}

    return () => {
      alive = false;
      try {
        bgListenerHandleRef.current?.remove?.();
      } catch {}
      bgListenerHandleRef.current = null;
    };
  }, []);

  useEffect(() => {
  if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

  const plugin = getAndroidGPSPlugin();
  if (!plugin?.addListener) return;

  let alive = true;

    const hb = plugin.addListener("gpsHeartbeat", (e) => {
      if (!alive) return;

      const ts = e?.ts || Date.now();

      lastAndroidFixTsRef.current = ts;

      liveLog("Android gpsHeartbeat", { ts });
      logGPS(null, "heartbeat", {
        ts,
        alive: true,
      });
    });

  return () => {
    alive = false;
    try {
      hb?.remove?.();
    } catch {}
  };
}, []);

  useEffect(() => {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

    const plugin = getAndroidGPSPlugin();
    if (!plugin?.addListener) return;

    let alive = true;

    try {
      plugin
        .addListener("gpsBuffer", (payload) => {
          if (!alive) return;
          const points = payload?.points;
          if (!Array.isArray(points) || !points.length) return;
          const now = Date.now();
          androidLastNativeBufferTsRef.current = now;
          if (!lastAndroidBufferLogTsRef.current || now - lastAndroidBufferLogTsRef.current > 3000) {
            lastAndroidBufferLogTsRef.current = now;
            liveLog("Android gpsBuffer", { count: points.length });
          }
          processAndroidBufferPoints(points, { source: "android-buffer" });
        })
        .catch(() => {});
    } catch {}

    return () => {
      alive = false;
    };
  }, []);

  // ---------------------------------------------------
  // ANDROID: flush de buffer (reutilizado em resume/visibility)
  // ---------------------------------------------------
  const ANDROID_BUFFER_DRAIN_BATCH = 60;
  const ANDROID_BUFFER_DRAIN_INTERVAL_MS = 16;

  const scheduleAndroidBufferDrain = () => {
    if (androidBufferDrainTimerRef.current) return;
    androidBufferDrainTimerRef.current = setTimeout(
      drainAndroidBufferQueue,
      ANDROID_BUFFER_DRAIN_INTERVAL_MS
    );
  };

  const drainAndroidBufferQueue = () => {
    androidBufferDrainTimerRef.current = null;
    const queue = androidBufferQueueRef.current;
    if (!queue.length) {
      if (androidBufferDrainEmitRef.current) {
        androidBufferDrainEmitRef.current = false;
        emit(lastFix, { force: true });
      }
      return;
    }
    const batch = queue.splice(0, ANDROID_BUFFER_DRAIN_BATCH);
    for (const p of batch) {
      handleFix(p, p.__src || "android-buffer");
    }
    if (queue.length) {
      scheduleAndroidBufferDrain();
    } else if (androidBufferDrainEmitRef.current) {
      androidBufferDrainEmitRef.current = false;
      emit(lastFix, { force: true });
    }
  };

  function processAndroidBufferPoints(points, opts = {}) {
    if (!Array.isArray(points) || !points.length) return;

    const source = opts.source || "android-buffer";
    const emitBufferEvent = opts.emitBufferEvent !== false;

    const toSeq = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const sorted = [...points].sort(
      (a, b) => (toSeq(a?.seq) ?? 0) - (toSeq(b?.seq) ?? 0)
    );

    const seqs = sorted.map((p) => toSeq(p?.seq)).filter((v) => v != null);
    const firstSeq = seqs.length ? seqs[0] : null;
    const lastSeq = seqs.length ? seqs[seqs.length - 1] : null;

    // Se o serviço reiniciou e o seq voltou atrás, re-sincronizar.
    if (
      firstSeq != null &&
      lastSeqRef.current != null &&
      lastSeqRef.current > 0 &&
      firstSeq <= lastSeqRef.current - 5
    ) {
      const now = Date.now();
      if (now - androidSeqResetLogTsRef.current > 5000) {
        androidSeqResetLogTsRef.current = now;
        fgLog("⚠️ [ANDROID BG] seq reset detectado", {
          prev: lastSeqRef.current,
          firstSeq,
        });
      }
      lastSeqRef.current = firstSeq - 1;
      setLastSeq(lastSeqRef.current);
    }

    if (opts.logContext) {
      fgLog("⚡ [ANDROID BG] buffer points", {
        count: sorted.length,
        firstSeq,
        lastSeq,
        ...opts.logContext,
      });
      logGPS(null, "android-buffer", {
        event: opts.logContext.event || "buffer",
        count: sorted.length,
        firstSeq,
        lastSeq,
      });
      logEvent("gps", "Android buffer", {
        event: opts.logContext.event || "buffer",
        count: sorted.length,
        firstSeq,
        lastSeq,
      });
    }

    const normalized = sorted
      .map((p) => ({
        lat: p.lat,
        lon: p.lon,
        alt: p.ele ?? p.alt ?? null,
        acc: p.acc ?? null,
        speed: p.spd ?? p.speed ?? null,
        ts: normalizeTs(p.ts),
        seq: toSeq(p.seq),
        __src: source,
      }))
      .filter((p) => typeof p.lat === "number" && typeof p.lon === "number");

    const shouldDownsampleBg =
      (source === "android-buffer" || source === "android-bg") &&
      !appIsActiveRef.current;
    const BG_DOWNSAMPLE_MS = 1500;
    const downsampleByTime = (points, windowMs) => {
      if (!points.length) return points;
      const buckets = new Map();
      for (const p of points) {
        const ts = Number(p.ts) || 0;
        const key = windowMs ? Math.floor(ts / windowMs) : ts;
        const acc = Number.isFinite(p.acc) ? Number(p.acc) : null;
        const prev = buckets.get(key);
        if (!prev) {
          buckets.set(key, p);
          continue;
        }
        const prevAcc = Number.isFinite(prev.acc) ? Number(prev.acc) : null;
        if (acc == null && prevAcc != null) continue;
        if (prevAcc == null && acc != null) {
          buckets.set(key, p);
          continue;
        }
        if (acc != null && prevAcc != null && acc < prevAcc) {
          buckets.set(key, p);
        }
      }
      return Array.from(buckets.values()).sort((a, b) => (a.ts || 0) - (b.ts || 0));
    };

    const filtered = shouldDownsampleBg
      ? downsampleByTime(normalized, BG_DOWNSAMPLE_MS)
      : normalized;

    if (filtered.length) {
      const now = Date.now();
      if (!lastAndroidBufferInfoLogTsRef.current || now - lastAndroidBufferInfoLogTsRef.current > 10000) {
        lastAndroidBufferInfoLogTsRef.current = now;
        const tsVals = filtered.map((p) => p.ts).filter((v) => Number.isFinite(v));
        const minTs = tsVals.length ? Math.min(...tsVals) : null;
        const maxTs = tsVals.length ? Math.max(...tsVals) : null;
        logEvent("gps", "Android buffer ingest", {
          source,
          count: filtered.length,
          firstSeq,
          lastSeq,
          minTs,
          maxTs,
        });
      }
    }

    if (filtered.length) {
      androidBufferQueueRef.current.push(...filtered);
      androidBufferDrainEmitRef.current =
        androidBufferDrainEmitRef.current || emitBufferEvent;
      scheduleAndroidBufferDrain();

      // Se o WebView estiver em background, timers/setInterval podem ser
      // suspensos/adiados no Android — processar um pequeno lote imediato
      // para evitar que pontos fiquem presos no buffer até ao resume.
      // Isto é aplicado apenas no Android nativo.
      try {
        if (isBg() && isNativeCapacitor && Capacitor.getPlatform() === "android") {
          const IMMEDIATE_BG_BATCH = 30;
          const immediate = androidBufferQueueRef.current.splice(0, IMMEDIATE_BG_BATCH);
          if (immediate.length) {
            fgLog("⚡ [ANDROID BG] enqueuing immediate batch into pending buffer", { count: immediate.length });
            try {
              const bufferPoints = immediate
                .map((p) => ({
                  lat: p.lat,
                  lon: p.lon,
                  acc: p.acc,
                  ele: p.ele,
                  spd: p.spd,
                  ts: Number(p.ts) || Number(p.time) || Date.now(),
                  seq: typeof p.seq === "number" ? Number(p.seq) : null,
                  src: "android-bg",
                  fromBuffer: true,
                }))
                .filter((p) => typeof p.lat === "number" && typeof p.lon === "number");
              if (typeof window !== "undefined" && bufferPoints.length) {
                window.dispatchEvent(new CustomEvent("gps:buffer", { detail: { points: bufferPoints } }));
              }
            } catch (e) {}
          }
        }
      } catch {}
    }

    if (emitBufferEvent) {
      try {
      const bufferPoints = filtered
          .map((p) => ({
            lat: p.lat,
            lon: p.lon,
            acc: p.acc,
            ele: p.ele,
            spd: p.spd,
            ts: normalizeTs(p.ts),
            seq: toSeq(p.seq),
            src: source,
          }))
          .filter((p) => typeof p.lat === "number" && typeof p.lon === "number");

        if (bufferPoints.length) {
          if (typeof window !== "undefined") {
            const prev = Array.isArray(window.__TREKKIN_GPS_BUFFER)
              ? window.__TREKKIN_GPS_BUFFER
              : [];
            window.__TREKKIN_GPS_BUFFER = prev.concat(bufferPoints);
          }
          window.dispatchEvent(
            new CustomEvent("gps:buffer", { detail: { points: bufferPoints } })
          );
        }
      } catch {}
    }

    // emit será feito no fim do drain para evitar picos de CPU no resume
  }

  async function flushAndroidBuffer() {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

    const plugin = getAndroidGPSPlugin();
    if (!plugin?.flushBufferedLocations) return;

    try {
      const res = await plugin.flushBufferedLocations();
      liveLog("Android buffer flush", {
        count: Array.isArray(res?.points) ? res.points.length : 0,
      });
      if (!res?.points?.length) return;

      // collect some telemetry about the flushed batch (first/last ts/seq and deltas)
      try {
        const pts = (Array.isArray(res.points) ? res.points : []).map((p) => ({
          seq: toSeq(p.seq),
          ts: normalizeTs(p.ts),
          lat: p.lat,
          lon: p.lon,
          acc: p.acc,
        })).filter(p => p && p.seq != null && p.ts != null);
        if (pts.length) {
          const first = pts[0];
          const last = pts[pts.length - 1];
          const firstDelta = lastAndroidTsRef.current ? (lastAndroidTsRef.current - first.ts) : null;
          const lastDelta = lastAndroidTsRef.current ? (lastAndroidTsRef.current - last.ts) : null;
          logEvent("gps", "Android buffer flush detail", { count: pts.length, firstSeq: first.seq, lastSeq: last.seq, firstTs: first.ts, lastTs: last.ts, firstDelta, lastDelta });
        }
      } catch (e) {}

      // native-provided metadata (if any)
      try {
        if (res && res.meta) {
          const m = res.meta;
          logEvent("gps", "Android buffer flush native", { count: m.count, firstSeq: m.firstSeq, lastSeq: m.lastSeq, firstTs: m.firstTs, lastTs: m.lastTs, deltaMs: m.deltaMs, minAcc: m.minAcc, maxAcc: m.maxAcc });
        }
      } catch (e) {}

      processAndroidBufferPoints(res.points, {
        source: "android-buffer",
        logContext: { event: "flush" },
      });
    } catch {}
  }

  function startBackgroundFlushInterval() {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;
    if (backgroundFlushIntervalRef.current) return;

    backgroundFlushIntervalRef.current = setInterval(() => {
      flushAndroidBuffer();
    }, BG_BUFFER_FLUSH_INTERVAL_MS);
  }

  function stopBackgroundFlushInterval() {
    if (!backgroundFlushIntervalRef.current) return;
    clearInterval(backgroundFlushIntervalRef.current);
    backgroundFlushIntervalRef.current = null;
  }

  useEffect(() => {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

    const plugin = getAndroidGPSPlugin();
    if (!plugin?.flushBufferedLocations) return;

    // primeira execução
    flushAndroidBuffer();

    return () => {
      stopBackgroundFlushInterval();
    };
  }, []);

  // ---------------------------------------------------
  // ANDROID: flush imediato ao reabrir a app
  // ---------------------------------------------------
  useEffect(() => {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

    const plugin = getAndroidGPSPlugin();
    if (!plugin?.flushBufferedLocations) return;

    const sub = App.addListener("appStateChange", ({ isActive }) => {
      liveLog("Android appStateChange", { isActive });
      appIsActiveRef.current = !!isActive;
      const { isRecording, recordingStartTime, recordingStopTime } =
        usePanelsStore.getState();
      const hasRecordingStart = !!recordingStartTime && !recordingStopTime;
      const isRecordingEffective = !!isRecording || hasRecordingStart;
      if (!isRecording && hasRecordingStart) {
        logEvent("warn", "Android background: recording state mismatch", {
          isRecording,
          hasRecordingStart,
          recordingStartTime,
          recordingStopTime,
        });
      }
      const { isSharing } = useShareStore.getState();
      const groupState = useGroupStore.getState();
      const gid =
        typeof groupState?.currentGroup === "string"
          ? groupState.currentGroup
          : groupState?.currentGroup?.id || null;
      const { events } = useEventsStore.getState() || {};
      const { isLive } = getActiveEventForGroupSafe(
        groupState?.groups,
        gid,
        events
      );

      if (!isActive) {
        startBackgroundFlushInterval();
        flushAndroidBuffer().finally(() => {
          emit(lastFix, { force: true });
        });

        // Keep BG service running on Android to avoid recording gaps.
        liveLog("Android background: keep service", {
          isRecording: isRecordingEffective,
          isSharing,
          isLive,
        });
        return;
      }

      stopBackgroundFlushInterval();

      try {
        startAndroidBGServiceIfPossible();
        const now = Date.now();
        const hasRecentNativeBuffer =
          androidLastNativeBufferTsRef.current &&
          now - androidLastNativeBufferTsRef.current < 3000;
        if (hasRecentNativeBuffer) {
          logEvent("system", "Android resume: skip flush (native buffer fresh)");
          liveLog("Android resume: skip flush (native buffer fresh)");
          emit(lastFix, { force: true });
          return;
        }
        logEvent("system", "Android resume: flush (skip getCurrentPositionOnce)");
        liveLog("Android resume: start service + flush");
        flushAndroidBuffer().finally(() => {
          emit(lastFix, { force: true });
        });
      } catch {
        emit(lastFix, { force: true });
      }
    });

    return () => {
      try {
        sub?.remove?.();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

    const plugin = getAndroidGPSPlugin();
    if (!plugin?.setBackgroundDispatch) return;

    const lastLiveTargetsRef = { current: null };
    const eventsLiveCacheRef = { current: null };
    const eventsLiveDbRef = ref(db, "/eventsLive");

    const isLiveEventGroupViaEventsStore = (gid) => {
      if (!gid) return false;
      const { events } = useEventsStore.getState() || {};
      const eventsArr = Array.isArray(events)
        ? events
        : events && typeof events === "object"
        ? Object.values(events)
        : [];
      if (!eventsArr.length) return false;
      return eventsArr.some((ev) => {
        if (!ev || ev.isLive !== true) return false;
        const egid = ev.groupId || ev.gid || ev.group || null;
        return (
          egid === gid ||
          (typeof egid === "string" &&
            typeof gid === "string" &&
            egid.trim() === gid.trim())
        );
      });
    };

    const isLiveEventGroupViaEventsLiveIndex = (gid, uid) => {
      if (!gid || !uid) return false;
      const payload = eventsLiveCacheRef.current || {};
      const entries = Object.entries(payload).map(([id, ev]) => ({
        id,
        ...(ev || {}),
      }));
      if (!entries.length) return false;

      const groupState = useGroupStore.getState();
      const groupsArr = Array.isArray(groupState?.groups)
        ? groupState.groups
        : groupState?.groups && typeof groupState.groups === "object"
        ? Object.values(groupState.groups)
        : [];

      const userEventsObj =
        useEventsStore.getState().userEvents &&
        typeof useEventsStore.getState().userEvents === "object"
          ? useEventsStore.getState().userEvents
          : {};

      const isUserConfirmedForEvent = (eventId) => {
        const ue = userEventsObj?.[eventId] || null;
        const status = ue?.status || ue?.state || ue?.registrationStatus;
        return status === "confirmed" || status === "active" || status === true;
      };

      // 1) Preferir eventos LIVE onde o user está confirmado
      const liveEventConfirmed =
        entries.find(
          (ev) =>
            ev?.isLive === true &&
            ev?.groupId === gid &&
            isUserConfirmedForEvent(ev.id || ev.eventId) &&
            groupsArr.some((g) => (g?.id === gid || g?.groupId === gid) && g?.members?.[uid])
        ) || null;

      if (liveEventConfirmed) return true;

      // 2) Fallback: se não há confirmação, mas o user é membro do grupo e o evento está LIVE
      const liveEventMember =
        entries.find(
          (ev) =>
            ev?.isLive === true &&
            ev?.groupId === gid &&
            groupsArr.some((g) => (g?.id === gid || g?.groupId === gid) && g?.members?.[uid])
        ) || null;

      return !!liveEventMember;
    };

    const resolveAndroidLiveGroupId = (uid) => {
      const groupState = useGroupStore.getState();
      const currentId =
        typeof groupState?.currentGroup === "string"
          ? groupState.currentGroup
          : groupState?.currentGroup?.id || null;
      if (currentId) return currentId;

      const groupsArr = Array.isArray(groupState?.groups)
        ? groupState.groups
        : groupState?.groups && typeof groupState.groups === "object"
        ? Object.values(groupState.groups)
        : [];

      if (!uid || !groupsArr.length) return null;

      const liveGroup =
        groupsArr.find(
          (g) =>
            (g?.activeEventId ||
              isLiveEventGroupViaEventsStore(g?.id || g?.groupId) ||
              isLiveEventGroupViaEventsLiveIndex(g?.id || g?.groupId, uid)) &&
            g?.members?.[uid] === true
        ) ||
        groupsArr.find(
          (g) =>
            (g?.activeEventId ||
              isLiveEventGroupViaEventsStore(g?.id || g?.groupId) ||
              isLiveEventGroupViaEventsLiveIndex(g?.id || g?.groupId, uid)) &&
            g?.members?.[uid]
        ) ||
        null;

      return (
        liveGroup?.id ||
        liveGroup?.groupId ||
        liveGroup?.firebaseId ||
        liveGroup?.key ||
        null
      );
    };

    const computeEnabled = () => {
      if (usePanelsStore.getState().recordingMode === "expedition") {
        return false;
      }
      const { shareId } = useShareStore.getState();
      if (shareId) return true;

      const uid =
        useAuthStore.getState().user?.uid || auth?.currentUser?.uid || null;
      const groupState = useGroupStore.getState();
      const gid = resolveAndroidLiveGroupId(uid);
      if (!gid) return false;
      const { events } = useEventsStore.getState() || {};
      const { isLive } = getActiveEventForGroupSafe(
        groupState?.groups,
        gid,
        events
      );
      return !!(
        isLive ||
        isLiveEventGroupViaEventsStore(gid) ||
        isLiveEventGroupViaEventsLiveIndex(gid, uid)
      );
    };

    const clearLiveTargetsTimer = () => {
      if (liveTargetsTimerRef.current) {
        clearTimeout(liveTargetsTimerRef.current);
        liveTargetsTimerRef.current = null;
      }
    };

    const scheduleLiveTargetsRefresh = (plugin) => {
      clearLiveTargetsTimer();
      const next = () => {
        refreshLiveTargets(plugin, { forceTokenRefresh: true }).finally(() => {
          liveTargetsTimerRef.current = setTimeout(next, 14 * 60 * 1000);
        });
      };
      next();
    };

    const fetchLiveToken = async (forceRefresh = false) => {
      // IMPORTANT:
      // `useAuthStore.user` é um perfil "plain object" (sem getIdToken).
      // Para LiveShare / LiveEvent em BG precisamos SEMPRE do Firebase Auth user real.
      const firebaseUser = auth?.currentUser || null;
      if (!firebaseUser?.getIdToken) return null;
      try {
        return await firebaseUser.getIdToken(!!forceRefresh);
      } catch {
        return null;
      }
    };

    const refreshLiveTargets = async (
      plugin,
      { forceTokenRefresh = false } = {}
    ) => {
      if (!plugin?.setLiveTargets) return;
      const profileUser = useAuthStore.getState().user || null;
      const firebaseUser = auth?.currentUser || null;
      const uid = firebaseUser?.uid || profileUser?.uid || null;
      if (!uid) return;
      const token = await fetchLiveToken(forceTokenRefresh);
      if (!token) {
        liveLog("Android live token missing");
        return;
      }
      const name =
        profileUser?.name ||
        profileUser?.firstName ||
        firebaseUser?.displayName ||
        null;
      const shareId = useShareStore.getState().shareId || null;
      const gid = resolveAndroidLiveGroupId(uid);

      // Para LIVE EVENT, há cenários onde `groups/<gid>/activeEventId` não existe
      // para participantes. Mesmo assim, se `events` indicar evento LIVE para esse
      // grupo, devemos manter `groupId` no nativo para permitir dispatch em BG.
      const shouldEnableGroupTarget =
        !!gid &&
        (isLiveEventGroupViaEventsStore(gid) ||
          isLiveEventGroupViaEventsLiveIndex(gid, uid));

      const nextTargets = {
        enabled: true,
        uid,
        groupId: shouldEnableGroupTarget ? gid : null,
        shareId,
        token,
        name,
        // Android BG: permitir refresh do ID token via refreshToken (quando JS está pausado)
        apiKey: "AIzaSyBAw86VN77OgmU_1g2lTITz0yfI8QDh6k4",
        refreshToken:
          typeof firebaseUser?.stsTokenManager?.refreshToken === "string"
            ? firebaseUser.stsTokenManager.refreshToken
            : typeof firebaseUser?.refreshToken === "string"
            ? firebaseUser.refreshToken
            : null,
      };

      const prev = lastLiveTargetsRef.current;
      const changed =
        !prev ||
        prev.uid !== nextTargets.uid ||
        prev.groupId !== nextTargets.groupId ||
        prev.shareId !== nextTargets.shareId ||
        prev.token !== nextTargets.token ||
        prev.name !== nextTargets.name ||
        prev.apiKey !== nextTargets.apiKey ||
        prev.refreshToken !== nextTargets.refreshToken ||
        prev.enabled !== nextTargets.enabled;

      if (!changed) return;

      lastLiveTargetsRef.current = nextTargets;
      liveTokenRef.current = token;

      plugin.setLiveTargets(nextTargets);
    };

    const updateDispatch = () => {
      const enabled = computeEnabled();
      if (enabled === androidLiveDispatchRef.current) return;
      androidLiveDispatchRef.current = enabled;
      try {
        plugin.setBackgroundDispatch({ enabled });
        liveLog("Android background dispatch", { enabled });
        if (enabled && !androidBatteryOptPromptedRef.current) {
          androidBatteryOptPromptedRef.current = true;
          plugin
            .isIgnoringBatteryOptimizations?.()
            .then((res) => {
              const ignoring = !!res?.ignoring;
              if (!ignoring) {
                plugin.requestIgnoreBatteryOptimizations?.();
              }
            })
            .catch(() => {});
        }
        if (enabled) {
          lastLiveTargetsRef.current = null;
          scheduleLiveTargetsRefresh(plugin);
        } else {
          liveTokenRef.current = null;
          lastLiveTargetsRef.current = null;
          clearLiveTargetsTimer();
          // Se nem LiveShare nem Live Event estão ativos, limpar targets no nativo
          // (evita continuar a enviar localização para um share/group antigo).
          try {
            plugin?.setLiveTargets?.({ enabled: false });
          } catch {}
        }
      } catch {}
    };

    updateDispatch();

    const onLiveStateChange = () => {
      updateDispatch();
      if (androidLiveDispatchRef.current) {
        // Refresh targets immediately on group/share changes (token refresh remains periodic).
        refreshLiveTargets(plugin, { forceTokenRefresh: false }).catch(() => {});
      }
    };

    const unsubShare = useShareStore.subscribe(() => onLiveStateChange());
    const unsubGroup = useGroupStore.subscribe(() => onLiveStateChange());
    const unsubEvents = useEventsStore.subscribe(() => onLiveStateChange());
    const unsubPanels = usePanelsStore.subscribe(() => onLiveStateChange());

    // Para participantes, o LIVE pode não estar refletido em `groups/<gid>/activeEventId`.
    // O source-of-truth do LIVE é `eventsLive/*` (index). Mantemos um cache local (onValue)
    // e chamamos `onLiveStateChange()` quando o snapshot muda para garantir setLiveTargets.
    onValue(
      eventsLiveDbRef,
      (snap) => {
        eventsLiveCacheRef.current = snap.val() || {};
        onLiveStateChange();
      },
      () => {}
    );

    return () => {
      try {
        unsubShare?.();
        unsubGroup?.();
        unsubEvents?.();
        unsubPanels?.();
      } catch {}
      try {
        off(eventsLiveDbRef);
      } catch {}
    };
  }, []);

  // ---------------------------------------------------
  // Native external listener — iOS/Swift bridge + compat "native:location" (INALTERADO)
  // ---------------------------------------------------
  useEffect(() => {
    function fromNativeGpsUpdate(ev) {
      const d = ev.detail || {};
      if (typeof d.lat !== "number" || typeof d.lon !== "number") return;

      isNativeRef.current = true;

      const rawAlt =
        typeof d.alt === "number" ? d.alt : typeof d.ele === "number" ? d.ele : null;

      const rawSpeed =
        typeof d.spd === "number" ? d.spd : typeof d.speed === "number" ? d.speed : null;

      const fix = {
        lat: d.lat,
        lon: d.lon,
        alt: Number.isFinite(rawAlt) ? rawAlt : null,
        acc: Number.isFinite(d.acc) ? d.acc : null,
        speed: Number.isFinite(rawSpeed) ? rawSpeed : null,
        ts: d.ts || d.time || nowTs(),
      };

      handleFix(fix, "native-bridge");
    }

    function fromNativeLocation(ev) {
      const d = ev.detail || {};
      if (typeof d.lat !== "number" || typeof d.lon !== "number") return;

      isNativeRef.current = true;

      const rawAlt =
        typeof d.alt === "number" ? d.alt : typeof d.ele === "number" ? d.ele : null;

      const rawSpeed =
        typeof d.spd === "number" ? d.spd : typeof d.speed === "number" ? d.speed : null;

      const fix = {
        lat: d.lat,
        lon: d.lon,
        alt: Number.isFinite(rawAlt) ? rawAlt : null,
        acc: Number.isFinite(d.acc) ? d.acc : null,
        speed: Number.isFinite(rawSpeed) ? rawSpeed : null,
        ts: d.ts || d.time || nowTs(),
      };

      handleFix(fix, "native");
    }

    try {
      window.addEventListener("native-gps-update", fromNativeGpsUpdate);
      window.addEventListener("native:location", fromNativeLocation);
    } catch {}

    return () => {
      try {
        window.removeEventListener("native-gps-update", fromNativeGpsUpdate);
        window.removeEventListener("native:location", fromNativeLocation);
      } catch {}
    };
  }, []);

    // ---------------------------------------------------
    // acceptFix / handleFix (CORRIGIDO)
    // ---------------------------------------------------
    function handleFix(fix, source = "unknown") {
      if (!fix.__src) fix.__src = source;
      
      // 1. FILTRO DE SEQUÊNCIA (ANDROID)
      //    ✅ Não usa lastSeqRef global (evita colisões com iOS/Web)
      //    ✅ Permite backfill do buffer mesmo que chegue depois (BG real)
      if (source === "android-bg" || source === "android-buffer") {
        let seqNum = Number(fix.seq);

        // Se o ponto não trouxer seq, sintetiza a partir do timestamp (ou Date.now)
        if (!Number.isFinite(seqNum)) {
          const tsGuess = Number(fix.ts || fix.time || fix.timestamp || 0);
          seqNum =
            Number.isFinite(tsGuess) && tsGuess > 0 ? tsGuess : Date.now();
          fix.seq = seqNum;
          fix.__seqSynth = true;
        } else {
          fix.seq = seqNum;
        }

        // Evitar apenas duplicados imediatos.
        // Nota: em BG, o JS pode "acordar" e receber backfill com seq <= watermark.
        if (seqNum <= lastAndroidSeqRef.current) {
          const allowBackfill = source === "android-buffer";
          if (!allowBackfill) {
            liveLog("ANDROID: duplicate/out-of-order seq, drop", {
              seq: seqNum,
              lastAndroidSeq: lastAndroidSeqRef.current,
              source,
            });
            return;
          }
        }

        // Watermark Android: só avança para a frente
        if (seqNum > lastAndroidSeqRef.current) {
          lastAndroidSeqRef.current = seqNum;
          setLastAndroidSeq(seqNum);
        }
      }
// 2. VALIDAÇÃO NORMAL (sem alterações)
      if (
        typeof fix?.lat !== "number" ||
        typeof fix?.lon !== "number" ||
        !Number.isFinite(fix.lat) ||
        !Number.isFinite(fix.lon)
      ) {
        logGPS(fix, source, { accepted: false, reason: "invalid_coords" });
        return;
      }

      setLastFix(fix);
      setAccuracy(fix.acc);

      if (isAndroidPlatform) {
        const rawTs = Number(fix.ts) || 0;
        const ts = rawTs && rawTs < 1e12 ? rawTs * 1000 : rawTs;
        if (ts && ts !== fix.ts) {
          fix.ts = ts;
        }
        if (ts && lastAndroidTsRef.current && ts < lastAndroidTsRef.current) {
          const deltaMs = lastAndroidTsRef.current - ts;
          const isBuffered = source === "android-bg" || source === "android-buffer";
          const ANDROID_BUFFER_OOO_MS = 15 * 60 * 1000; // tolera backlog real de BG (15 min)
          if (!isBuffered) {
            logGPS(fix, source, {
              accepted: false,
              reason: "android_out_of_order_ts",
              deltaMs,
            });
            emit(fix);
            return;
          }
          // para pontos buffered: aceitar mesmo com grandes atrasos (BG real), apenas marcar
          if (deltaMs > ANDROID_BUFFER_OOO_MS) {
            fix.__bufferedLargeDelay = true;
            logGPS(fix, source, {
              accepted: true,
              reason: "android_buffer_large_delay",
              deltaMs,
            });
          }
          logGPS(fix, source, {
            accepted: true,
            reason: "android_out_of_order_ts_buffered",
            deltaMs,
          });
          try {
            logEvent("gps", "Android android_out_of_order_ts_buffered_delta", { deltaMs, seq: fix.seq || null, ts: fix.ts || null, src: source });
          } catch {}
        }
        if (ts) {
          lastAndroidTsRef.current = ts;
          setLastAndroidTs(ts);
        }
      }

      const liveTs = Number(fix.ts) || 0;
      if (!liveTs || liveTs >= (lastLiveFixTsRef.current || 0)) {
        lastLiveFixTsRef.current = liveTs || Date.now();
        setLastLiveFix(fix);
      }

      const inWarmup = ++fixCountRef.current <= CFG.androidWarmupFixes;

      if (inWarmup && isAndroidPlatform) {
        if (typeof fix.acc !== "number" || fix.acc > CFG.androidWarmupRequiredAcc) {
          tickTime(fix);
          logGPS(fix, source, { accepted: false, reason: "android_warmup_accuracy" });
          return emit(fix);
        }
      } else if (!inWarmup) {
        if (waitingAccurate.current) {
          if (!fix.acc || fix.acc > CFG.resumeRequiredAccuracy) {
            tickTime(fix);
            logGPS(fix, source, { accepted: false, reason: "awaiting_accurate" });
            return emit(fix);
          }
          waitingAccurate.current = false;
        }

        if (fix.acc && fix.acc > CFG.hardAccuracyReject) {
          tickTime(fix);
          logGPS(fix, source, { accepted: false, reason: "hard_accuracy_reject" });
          return emit(fix);
        }

        if (
          isAndroidPlatform &&
          typeof fix.acc === "number" &&
          fix.acc > CFG.androidMaxAcceptableAcc
        ) {
          tickTime(fix);
          logGPS(fix, source, { accepted: false, reason: "android_accuracy_filter" });
          return emit(fix);
        }
      }

      // 3. ACEITAR E EMITIR (para TODOS os pontos válidos)
      acceptFix(fix, source);
      logGPS(fix, source, { accepted: true, reason: "accepted" });
      
      // A função emit() agora é chamada para todos, incluindo os do buffer,
      // o que vai fazer com que o mapa seja atualizado.
      emit(fix);
    }

  function acceptFix(fix, source) {
    const arr = pathRef.current;
    const prev = arr[arr.length - 1];
    const jitterIgnoreDistance = getJitterIgnoreDistance();

    let step = 0;
    let derivedSpeed = null;
    const reportedSpeed = Number.isFinite(fix.speed) ? fix.speed : null;

    if (prev) {
      step = haversine(prev, fix);
      const dt = (fix.ts - prev.ts) / 1000;
      if (dt > 0 && step > jitterIgnoreDistance) derivedSpeed = step / dt;
    }

    fix.speed = derivedSpeed != null ? Math.max(derivedSpeed, fix.speed ?? 0) : fix.speed;

    const moving =
      fix.speed != null ? fix.speed >= getSpeedMovingThreshold() : step >= CFG.minStepDistance;

    tickTime(fix, moving);

    // iOS/Web (ou Android em modo iOS) micro-step filter
    if ((!isAndroidPlatform || useAndroidIosFilters()) && prev && step < getMicroStepDistance()) {
      logGPS(fix, source, { accepted: false, reason: "ios_micro_step_filter", step });
      return;
    }

    if (prev) {
      if (step < jitterIgnoreDistance) {
        logGPS(fix, source, { accepted: false, reason: "jitter_small_step", step });
        return;
      }

      const speedNow = fix.speed ?? 0;
      const isVerySmallStep = step < CFG.smallStepDistance;
      const isSlow = speedNow < CFG.lowSpeedThreshold;
      const isBadAcc = typeof fix.acc === "number" && fix.acc > CFG.badAccuracy;

      if (isVerySmallStep && isSlow && isBadAcc) {
        logGPS(fix, source, { accepted: false, reason: "zigzag_filter", step, speedNow });
        return;
      }

      // Gap detector (mantido)
      const gap = (fix.ts - prev.ts) / 1000;
      if (gap >= CFG.longPauseGapSeconds) {
        logGPS(fix, source, { event: "long_gap_detected", gapSeconds: gap });
        if (step >= CFG.longPauseGapDistance) {
          logGPS(fix, source, { event: "long_gap_break", gapSeconds: gap, step });
          fix.gap = true;
          if (isAndroidPlatform) androidSmoothWindowRef.current = [];
        }
      }

      // Android extra filters (mantido)
      if (isAndroidPlatform && !useAndroidIosFilters()) {
      const androidRelaxed =
        !!CFG.androidRelaxedFilters ||
        source === "android-buffer" ||
        (source === "android-bg" && !appIsActiveRef.current) ||
        fix.__bufferedLargeDelay === true;

      if (!androidRelaxed) {
        const dt = (fix.ts - prev.ts) / 1000;
        const speedNowMps =
          Number.isFinite(fix.speed) ? fix.speed : dt > 0 ? step / dt : 0;
        const prevSpeedMps = Number.isFinite(prev.speed) ? prev.speed : null;

        if (Number.isFinite(speedNowMps) && speedNowMps > CFG.androidMaxSpeedMps) {
          logGPS(fix, source, {
            accepted: false,
            reason: "android_max_speed",
            speedNowMps,
          });
          emit(fix);
          return;
        }

        if (
          prevSpeedMps != null &&
          Number.isFinite(speedNowMps) &&
          dt > 0 &&
          dt <= 2
        ) {
          const accel = (speedNowMps - prevSpeedMps) / dt;
          if (accel > CFG.androidMaxAccelMps2) {
            logGPS(fix, source, {
              accepted: false,
              reason: "android_accel_jump",
              accel,
              speedNowMps,
              prevSpeedMps,
            });
            emit(fix);
            return;
          }
        }

        // Parado: rejeitar drift grosseiro quando o GPS diz "quase parado"
        if (
          speedNow < CFG.androidStationarySpeedThreshold &&
          typeof fix.acc === "number" &&
          fix.acc > CFG.androidStationaryMaxAcc &&
          step > CFG.androidStationaryMaxStep
        ) {
          logGPS(fix, source, {
            accepted: false,
            reason: "android_stationary_drift",
            step,
            speedNow,
          });
          emit(fix);
          return;
        }

        if (
          step < CFG.androidVerySmallStep &&
          typeof fix.acc === "number" &&
          fix.acc > CFG.androidMaxAcceptableAcc + 10
        ) {
          logGPS(fix, source, { accepted: false, reason: "android_small_step_bad_acc", step });
          emit(fix);
          return;
        }

        const slowByReported =
          reportedSpeed != null && reportedSpeed < CFG.androidSlowSpeedThreshold;
        if ((speedNow < CFG.androidSlowSpeedThreshold || slowByReported) &&
            step > CFG.androidBigJumpWhenSlow) {
          logGPS(fix, source, {
            accepted: false,
            reason: "android_big_jump_slow",
            step,
            speedNow,
          });
          emit(fix);
          return;
        }

        const beforePrev = arr.length > 1 ? arr[arr.length - 2] : null;
        if (beforePrev) {
          const dLat1 = prev.lat - beforePrev.lat;
          const dLon1 = prev.lon - beforePrev.lon;
          const dLat2 = fix.lat - prev.lat;
          const dLon2 = fix.lon - prev.lon;

          const mag1 = Math.sqrt(dLat1 * dLat1 + dLon1 * dLon1);
          const mag2 = Math.sqrt(dLat2 * dLat2 + dLon2 * dLon2);

          if (mag1 > 0 && mag2 > 0) {
            const dot = dLat1 * dLat2 + dLon1 * dLon2;
            const cosTheta = dot / (mag1 * mag2);

            if (cosTheta < 0.8 && step < 6 && speedNow < 1.2) {
              logGPS(fix, source, {
                accepted: false,
                reason: "android_dir_filter",
                step,
                cosTheta,
                speedNow,
              });
              emit(fix);
              return;
            }
          }
        }
      }
      }
    }

    // 🧭 iOS: usar bearing GPS quando em movimento
    if (!isAndroidPlatform && fix.speed != null && fix.speed >= 1.2 && prev) {
      const bearing =
        (Math.atan2(
          fix.lon - prev.lon,
          fix.lat - prev.lat
        ) * 180) / Math.PI;

      headingRef.current = (bearing + 360) % 360;
    }

    // Android smoothing (mantido)
    let finalFix = fix;
    if (isAndroidPlatform) {
      const win = androidSmoothWindowRef.current;
      win.push(fix);
      clamp(win, CFG.androidSmoothWindow);

      if (win.length >= 2) {
        const latAvg = avg(win.map((p) => p.lat));
        const lonAvg = avg(win.map((p) => p.lon));
        const altAvgArr = win.map((p) => p.alt).filter((v) => typeof v === "number");
        const spdAvgArr = win.map((p) => p.speed).filter((v) => typeof v === "number");

        finalFix = {
          ...fix,
          lat: latAvg,
          lon: lonAvg,
          alt: altAvgArr.length ? avg(altAvgArr) : fix.alt ?? null,
          speed: spdAvgArr.length ? avg(spdAvgArr) : fix.speed ?? null,
        };
      }
    }

    const smoothedAlt = smoothAlt(finalFix);
    const fixToStore =
      smoothedAlt != null ? { ...finalFix, alt: smoothedAlt } : finalFix;
    fixToStore.src = source;

    arr.push(fixToStore);
    if (!isAndroidPlatform) {
      setPathVersion((v) => v + 1);
    } else {
      const now = Date.now();
      if (now - lastPathUiEmitRef.current >= 1000) {
        lastPathUiEmitRef.current = now;
        setPathVersion((v) => v + 1);
      }
    }

    distRef.current += arr.length > 1 && step >= jitterIgnoreDistance ? step : 0;

    if (usePanelsStore.getState().recordingMode !== "expedition") {
      logGPSFixRemote({
        lat: fixToStore.lat,
        lon: fixToStore.lon,
        ele: fixToStore.alt,
        spd: fixToStore.speed,
        acc: fixToStore.acc,
        ts: fixToStore.ts,
        src: source,
      });
    }
  }

  // ---------------------------------------------------
  // tickTime
  // ---------------------------------------------------
  function tickTime(fix, movingMaybe) {
    const now = fix?.ts ?? nowTs();
    const last = tickTs.current ?? now;
    const dt = Math.max(0, (now - last) / 1000);
    tickTs.current = now;

    let moving = movingMaybe;
    if (moving == null && fix?.speed != null) moving = fix.speed >= getSpeedMovingThreshold();

    if (moving) {
      movRef.current += dt;
      lastBelow.current = null;
    } else {
      const since = lastBelow.current;
      if (since == null) lastBelow.current = now;
      else {
        const stopped = (now - since) / 1000;
        if (stopped >= CFG.speedStopGraceSeconds) stopRef.current += dt;
      }
    }
  }

  // ---------------------------------------------------
  // smoothAlt
  // ---------------------------------------------------
  function smoothAlt(fix) {
    if (fix.alt == null) return null;
    const w = altWin.current;
    w.push(fix.alt);
    clamp(w, CFG.elevationSmoothingWindow);

    const s = avg(w);

    if (prevSmoothAlt.current != null) {
      const d = s - prevSmoothAlt.current;
      if (Math.abs(d) >= CFG.elevationMinDelta) {
        if (d > 0) gainRef.current += d;
        else lossRef.current += -d;
      }
    }

    prevSmoothAlt.current = s;
    return s;
  }

  // ---------------------------------------------------
  // Visibilidade (APENAS web/iOS) — Android BG NÃO depende disto
  // ---------------------------------------------------
  useEffect(() => {
    function vis() {
      if (!isTracking) return;

      // ✅ Android nativo: não mexer em nada aqui
      if (isNativeCapacitor && Capacitor.getPlatform() === "android") {
        return;
      }

      if (pageHidden()) startFallback();
      else {
        stopFallback();
        waitingAccurate.current = true;
      }
    }

    try {
      document.addEventListener("visibilitychange", vis);
    } catch {}

    return () => {
      try {
        document.removeEventListener("visibilitychange", vis);
      } catch {}
    };
  }, [isTracking]);

  // ---------------------------------------------------
  // Cleanup inicial
  // ---------------------------------------------------
  useEffect(() => {
    enableMotionAwake();

    return () => {
      try {
        if (watchId.current != null && navigator?.geolocation) {
          navigator.geolocation.clearWatch(watchId.current);
        }
      } catch {}

      try {
        if (androidWarmupRetryTimerRef.current) {
          clearTimeout(androidWarmupRetryTimerRef.current);
          androidWarmupRetryTimerRef.current = null;
        }
      } catch {}

      try {
        if (androidStartupRetryTimerRef.current) {
          clearTimeout(androidStartupRetryTimerRef.current);
          androidStartupRetryTimerRef.current = null;
        }
      } catch {}

      try {
        if (Capacitor.getPlatform() === "android") stopNativeCompass();
      } catch {}

      stopAndroidBGServiceIfPossible();
      stopCapacitorWatch();
      stopFallback();

      try {
        bgListenerHandleRef.current?.remove?.();
      } catch {}
      bgListenerHandleRef.current = null;
      bgServiceActiveRef.current = false;
    };
  }, []);

  // Heartbeat BlackBox (opcional)
  useEffect(() => {
    if (!debugConfigRef.current.enabled) return;

    const t = setInterval(() => {
      if (!isTracking) return;

      logGPS(null, "heartbeat", {
        lastFixTs: lastFix?.ts ?? null,
        deltaSeconds: lastFix?.ts ? Math.round((Date.now() - lastFix.ts) / 1000) : null,
        bg: isBg(),
        serviceActive: bgServiceActiveRef.current,
      });
    }, 15000);

    return () => clearInterval(t);
  }, [isTracking, lastFix]);

  // ✅ Watchdog Android (SEMPRE ativo quando tracking, independente do BlackBox)
  useEffect(() => {
    if (!isNativeCapacitor || Capacitor.getPlatform() !== "android") return;

    const t = setInterval(() => {
      if (!isTracking) return;

      const lastTs = lastAndroidFixTsRef.current;
      if (!lastTs) return; // ainda não houve nenhum fix → não reiniciar

      const delta = Date.now() - lastTs;
      if (delta > 20000) {
        fgWarn("♻️ [ANDROID BG] Sem fixes >20s → restart service");
        const ok = startAndroidBGServiceIfPossible();
        if (!ok) bgServiceActiveRef.current = false;
      }
    }, 5000); // watchdog mais rápido, mas leve

    return () => clearInterval(t);
  }, [isTracking]);

  return {
    isTracking,
    lastFix,
    lastLiveFix,
    path,
    totalDistance: distRef.current,
    movingTime: movRef.current,
    stoppedTime: stopRef.current,
    elevationGain: gainRef.current,
    elevationLoss: lossRef.current,
    accuracyNow: accuracy,
    startTracking,
    stopTracking,
    requestAndroidAlwaysPermission,
    requestAndroidForegroundPermission,
  };
}
